import { Button } from '@/components/ui/button'
import { Drawer, DrawerContentRight } from '@/components/ui/drawer'
import { SectionHeading } from '@/components/ui/section'
import { ListRecordLayout } from '@/components/ListRecordLayout/ListRecordLayout'
import { useCanEditWorkspace } from '@/hooks/useCanEditWorkspace'
import { useSettingsPanelState } from '@/hooks/useSettingsPanelState'
import { useStytchWorkspaceMembers } from '@/hooks/useStytchWorkspaceMembers'
import type { Member } from '@stytch/vanilla-js'
import { LayoutGrid, Plus, Table2 } from 'lucide-react'

import { UserRecordPanel } from './UserRecordPanel'
import { UsersCards } from './UsersCards'
import { UsersTable } from './UsersTable'

export const Users = () => {
	const canEdit = useCanEditWorkspace()
	const { members, isLoading } = useStytchWorkspaceMembers()
	const { state, openRecord, openCreate, closePanel, setView } = useSettingsPanelState('users')

	const selectedUser = state.recordId
		? members?.find((m) => m.member_id === state.recordId)
		: undefined

	const handleSelectUser = (member: Member) => {
		openRecord(member.member_id)
	}

	const handleAddUser = () => {
		openCreate()
	}

	const listPanel = (
		<div className="flex flex-col h-full">
			{/* Header with title and controls in one line */}
			<div className="flex items-center justify-between mb-4">
				<SectionHeading>Users</SectionHeading>
				<div className="flex items-center gap-3">
					{/* View Toggle */}
					<div className="flex border rounded-md">
						<Button
							variant={state.view === 'table' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('table')}
							className="rounded-r-none border-r"
							aria-label="Table view"
							title="Table view"
						>
							<Table2 className="h-4 w-4" />
						</Button>
						<Button
							variant={state.view === 'cards' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('cards')}
							className="rounded-l-none"
							aria-label="Cards view"
							title="Cards view"
						>
							<LayoutGrid className="h-4 w-4" />
						</Button>
					</div>
					{canEdit && (
						<Button onClick={handleAddUser} size="sm" className="cursor-pointer">
							<Plus className="h-4 w-4 mr-2" />
							Add User
						</Button>
					)}
				</div>
			</div>

			{/* Content */}
			<div className="flex-1 overflow-auto">
				{isLoading ? (
					<div className="text-center py-8 text-muted-foreground">Loading users...</div>
				) : state.view === 'table' ? (
					<UsersTable
						members={members || []}
						onSelectUser={handleSelectUser}
						onAddUser={handleAddUser}
						selectedUserId={state.recordId || undefined}
					/>
				) : (
					<UsersCards
						members={members || []}
						onSelectUser={handleSelectUser}
						onAddUser={handleAddUser}
						selectedUserId={state.recordId || undefined}
					/>
				)}
			</div>
		</div>
	)

	return (
		<div className="flex flex-col h-full">
			{listPanel}
			<Drawer open={state.isOpen} onOpenChange={(open) => !open && closePanel()} direction="right">
				<DrawerContentRight>
					<UserRecordPanel user={selectedUser} isNew={state.mode === 'new'} onClose={closePanel} />
				</DrawerContentRight>
			</Drawer>
		</div>
	)

	const recordPanel = (
		<UserRecordPanel
			onClose={closePanel}
			user={selectedUser}
			isNew={state.mode === 'new'}
		/>
	)

	return <ListRecordLayout listPanel={listPanel} recordPanel={recordPanel} />
}
